export const EXAM_TABLE_WIDTH = {
  normal: 380,
  expanded: 700,
};

export const BULK_CHECKBOX_WIDTH = 18;

export const DRAWER_TRANSITION_MS = 400;
export const DRAWER_TRANSITION = `width ${DRAWER_TRANSITION_MS}ms ease-in-out`;

export const getTableTransitionStyle = (drawerWidth) => ({
  tableLayout: 'fixed',
  width: `${drawerWidth}px`,
  transition: DRAWER_TRANSITION,
});

const addPx = (...values) => `${values.reduce((sum, value) => sum + parseInt(value, 10), 0)}px`;

const EXPANDED_BASE_COLUMNS = {
  expand: 24,
  name: 140,
  dob: 100,
  patientId: 140,
  icons: 60,
  date: 110,
  risk: 45,
  actions: 50,
};

const NORMAL_COLUMNS = {
  expand: 24,
  name: 140,
  icons: 45,
  date: 95,
  risk: 35,
  actions: 33,
};

const FLEX_COLUMNS = ['name', 'patientId'];

const toPxColumns = (columns) =>
  Object.fromEntries(Object.entries(columns).map(([key, value]) => [key, `${value}px`]));

const scaleColumnsToWidth = (columns, targetWidth) => {
  const entries = Object.entries(columns);
  const baseTotal = entries.reduce((sum, [, value]) => sum + value, 0);
  const scaled = entries.map(([key, value]) => [key, Math.floor((value / baseTotal) * targetWidth)]);

  let remainder = targetWidth - scaled.reduce((sum, [, value]) => sum + value, 0);
  for (let i = 0; remainder > 0; i += 1, remainder -= 1) {
    scaled[i % scaled.length][1] += 1;
  }

  return Object.fromEntries(scaled);
};

/** Shrink flexible columns (name / patientId) to free `amount` px. */
const shrinkFlexColumns = (columns, amount) => {
  if (amount <= 0) return columns;

  const next = { ...columns };
  const flexKeys = FLEX_COLUMNS.filter((key) => next[key] != null);
  if (!flexKeys.length) return next;

  const flexTotal = flexKeys.reduce((sum, key) => sum + next[key], 0);
  let remaining = amount;

  flexKeys.forEach((key, index) => {
    const isLast = index === flexKeys.length - 1;
    const share = isLast ? remaining : Math.min(remaining, Math.floor((next[key] / flexTotal) * amount));
    const shrinkBy = Math.min(share, Math.max(0, next[key] - 40));
    next[key] -= shrinkBy;
    remaining -= shrinkBy;
  });

  for (const key of flexKeys) {
    if (remaining <= 0) break;
    const available = Math.max(0, next[key] - 40);
    const take = Math.min(available, remaining);
    next[key] -= take;
    remaining -= take;
  }

  return next;
};

export const getExamTableColumnWidths = (
  expandedPatientList,
  drawerWidth = null,
  { bulkAction = false } = {},
) => {
  const fallbackWidth = expandedPatientList ? EXAM_TABLE_WIDTH.expanded : EXAM_TABLE_WIDTH.normal;
  const targetWidth = drawerWidth ?? fallbackWidth;
  const baseColumns = expandedPatientList ? EXPANDED_BASE_COLUMNS : NORMAL_COLUMNS;
  const scaled = scaleColumnsToWidth(baseColumns, targetWidth);
  const columns = bulkAction ? shrinkFlexColumns(scaled, BULK_CHECKBOX_WIDTH) : scaled;

  return {
    tableWidth: `${targetWidth}px`,
    ...(bulkAction ? { bulk: `${BULK_CHECKBOX_WIDTH}px` } : {}),
    ...toPxColumns(columns),
  };
};

export const getWorklistColumnWidths = (expandedPatientList, drawerWidth = null) => {
  const exam = getExamTableColumnWidths(expandedPatientList, drawerWidth);

  return {
    tableWidth: exam.tableWidth,
    status: '18px',
    expand: exam.expand,
    patient_name: expandedPatientList ? exam.name : addPx(exam.name, exam.expand),
    patient_birth_date: exam.dob,
    patient_id: exam.patientId,
    scheduled_date: addPx(exam.date, exam.risk, exam.actions),
  };
};

export const getColumnCellStyle = (width, { noPadding = false } = {}) => ({
  width,
  minWidth: width,
  maxWidth: width,
  boxSizing: 'border-box',
  ...(noPadding ? { padding: 0, overflow: 'hidden' } : {}),
});
